import React, { useRef, useState } from "react";
import { Upload, X, ArrowLeft, ArrowRight, FileText, Loader2 } from "lucide-react";
import { api, assetUrl } from "../../adminApi";

export function ImageUploader({ images = [], onChange, testid = "image-uploader" }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const input = useRef(null);

  const pick = async (e) => {
    const files = e.target.files;
    if (!files || !files.length) return;
    setErr("");
    setBusy(true);
    try {
      const res = await api.upload(files);
      onChange([...images, ...res.files.map((f) => f.url)]);
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };

  const move = (i, d) => {
    const next = [...images];
    const j = i + d;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <span className="mono text-[9px] text-[#6B675F]">GÖRSELLER ({images.length})</span>
        <button
          type="button"
          onClick={() => input.current?.click()}
          disabled={busy}
          className="inline-flex items-center gap-2 mono text-[9px] border border-[#E2E0D8] bg-white px-4 py-2.5 hover:border-[#C8B29B] disabled:opacity-50 transition-colors"
          data-testid={`${testid}-btn`}
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} strokeWidth={1.7} />}
          {busy ? "YÜKLENİYOR..." : "GÖRSEL YÜKLE"}
        </button>
      </div>
      <input
        ref={input}
        type="file"
        accept="image/*"
        multiple
        onChange={pick}
        className="hidden"
        data-testid={`${testid}-input`}
      />
      {err && <p className="text-[12.5px] text-[#8C2E1D] mb-3">{err}</p>}
      {images.length === 0 ? (
        <div className="border border-dashed border-[#E2E0D8] bg-[#FAFAFA] py-10 text-center">
          <p className="mono text-[9px] text-[#A8A29A]">HENÜZ GÖRSEL YOK</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {images.map((src, i) => (
            <div key={`${src}-${i}`} className="relative group border border-[#E2E0D8] bg-[#FAFAFA]">
              <img src={assetUrl(src)} alt="" className="w-full h-[110px] object-cover" />
              <span className="absolute top-1.5 left-1.5 mono text-[8px] bg-white/90 px-1.5 py-0.5">
                {i === 0 ? "KAPAK" : String(i + 1).padStart(2, "0")}
              </span>
              <div className="absolute inset-x-0 bottom-0 flex opacity-0 group-hover:opacity-100 transition-opacity">
                <button type="button" onClick={() => move(i, -1)} className="flex-1 bg-white/95 py-1.5 flex justify-center">
                  <ArrowLeft size={12} />
                </button>
                <button type="button" onClick={() => move(i, 1)} className="flex-1 bg-white/95 py-1.5 flex justify-center border-x border-[#E2E0D8]">
                  <ArrowRight size={12} />
                </button>
                <button
                  type="button"
                  onClick={() => onChange(images.filter((_, k) => k !== i))}
                  className="flex-1 bg-white/95 py-1.5 flex justify-center text-[#8C2E1D]"
                  data-testid={`${testid}-remove-${i}`}
                >
                  <X size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function SingleImageUploader({ value = "", onChange, label = "GÖRSEL", testid = "single-image" }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const input = useRef(null);

  const pick = async (e) => {
    const files = e.target.files;
    if (!files || !files.length) return;
    setErr("");
    setBusy(true);
    try {
      const res = await api.upload([files[0]]);
      onChange(res.files[0].url);
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };

  return (
    <div data-testid={testid}>
      <span className="mono text-[9px] text-[#6B675F] block mb-2">{label}</span>
      <div className="flex items-center gap-4">
        <div className="w-[92px] h-[68px] border border-[#E2E0D8] bg-[#FAFAFA] overflow-hidden shrink-0">
          {value ? (
            <img src={assetUrl(value)} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className="mono text-[7.5px] text-[#A8A29A] flex items-center justify-center h-full">YOK</span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => input.current?.click()}
            disabled={busy}
            className="inline-flex items-center gap-2 mono text-[9px] border border-[#E2E0D8] bg-white px-4 py-2.5 hover:border-[#C8B29B] disabled:opacity-50 transition-colors"
            data-testid={`${testid}-btn`}
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} strokeWidth={1.7} />}
            {busy ? "YÜKLENİYOR..." : value ? "DEĞİŞTİR" : "YÜKLE"}
          </button>
          {value && (
            <button type="button" onClick={() => onChange("")} className="mono text-[9px] text-[#8C2E1D] px-2">
              KALDIR
            </button>
          )}
        </div>
      </div>
      <input ref={input} type="file" accept="image/*" onChange={pick} className="hidden" data-testid={`${testid}-input`} />
      {err && <p className="text-[12.5px] text-[#8C2E1D] mt-2">{err}</p>}
    </div>
  );
}

export function PdfUploader({
  value = "",
  fileName = "",
  updatedAt = "",
  status = "",
  onChange,
  testid = "pdf-uploader",
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const input = useRef(null);

  const pick = async (e) => {
    const files = e.target.files;
    if (!files || !files.length) return;
    setErr("");
    setBusy(true);
    try {
      const res = await api.upload([files[0]]);
      await onChange(res.files[0].url, res.files[0].filename || files[0].name);
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };

  const remove = async () => {
    if (!window.confirm("Bu koleksiyonun PDF katalogunu kaldırmak istediğinize emin misiniz?")) return;
    setErr("");
    setBusy(true);
    try {
      await onChange("", "");
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={testid} className="border border-[#E2E0D8] bg-[#FAFAFA] p-5">
      <div className="flex items-center justify-between mb-3 gap-4">
        <span className="mono text-[9px] text-[#6B675F]">PDF KATALOG</span>
        <span
          className={`mono text-[8.5px] ${value ? "text-[#3F6B4F]" : "text-[#A8A29A]"}`}
          data-testid={`${testid}-state`}
        >
          {value ? "PDF YÜKLÜ" : "PDF YOK"}
        </span>
      </div>

      {value && (
        <div className="flex items-start gap-3 mb-4">
          <FileText size={16} strokeWidth={1.7} className="mt-0.5 text-[#8C7862] shrink-0" />
          <div className="min-w-0">
            <p className="text-[13px] text-[#141414] break-all" data-testid={`${testid}-name`}>
              {fileName || value.split("/").pop()}
            </p>
            {updatedAt && (
              <p className="mono text-[8.5px] text-[#A8A29A] mt-1">
                GÜNCELLENDİ: {new Date(updatedAt).toLocaleString("tr-TR")}
              </p>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => input.current?.click()}
          disabled={busy}
          className="inline-flex items-center gap-2 mono text-[9px] border border-[#E2E0D8] bg-white px-4 py-2.5 hover:border-[#C8B29B] disabled:opacity-50 transition-colors"
          data-testid={`${testid}-btn`}
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} strokeWidth={1.7} />}
          {busy ? "YÜKLENİYOR..." : value ? "PDF DEĞİŞTİR" : "PDF YÜKLE"}
        </button>
        {value && (
          <>
            <a
              href={assetUrl(value)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 mono text-[9px] text-[#141414] link-underline"
              data-testid={`${testid}-view`}
            >
              <FileText size={12} strokeWidth={1.7} /> PDF'İ GÖRÜNTÜLE
            </a>
            <button
              type="button"
              onClick={remove}
              disabled={busy}
              className="mono text-[9px] text-[#8C2E1D] disabled:opacity-50"
              data-testid={`${testid}-clear`}
            >
              PDF'İ SİL
            </button>
          </>
        )}
        {status && (
          <span className="mono text-[8.5px] text-[#3F6B4F]" data-testid={`${testid}-status`}>
            {status}
          </span>
        )}
      </div>
      <input ref={input} type="file" accept="application/pdf" onChange={pick} className="hidden" data-testid={`${testid}-input`} />
      {err && <p className="text-[12.5px] text-[#8C2E1D] mt-2">{err}</p>}
      {!value && (
        <p className="mono text-[8.5px] text-[#A8A29A] mt-3">
          PDF eklenmezse indirme butonu ana MATCH kataloğunu kullanır. Maks. 25 MB.
        </p>
      )}
    </div>
  );
}
